/**
 * Initializes native mobile features when running in Capacitor
 * - Push notifications
 * - Keyboard handling
 * - Swipe-back navigation
 *
 * NOTE: Deep link handling is now in DeepLinkHandler.tsx, which is rendered
 * at the app root (outside auth gate) to handle links when logged out.
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isNative, initKeyboardListeners } from '@/lib/capacitor';
import { usePushNotifications } from '@/layout/usePushNotifications';

export function NativeInitializer() {
  const navigate = useNavigate();
  const location = useLocation();

  // Register for push notifications
  usePushNotifications();

  // Initialize keyboard handling
  useEffect(() => {
    if (!isNative()) return;
    initKeyboardListeners();
  }, []);

  // Swipe-back navigation
  useEffect(() => {
    if (!isNative()) return;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      touchStartTime = Date.now();
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX;
      const deltaY = Math.abs(touch.clientY - touchStartY);
      const deltaTime = Date.now() - touchStartTime;

      // Swipe from left edge (within 30px), right direction, more horizontal than vertical
      // Fast swipe (under 300ms) or longer swipe (over 100px)
      const isFromLeftEdge = touchStartX < 30;
      const isRightSwipe = deltaX > 50;
      const isHorizontal = deltaX > deltaY * 1.5;
      const isFastSwipe = deltaTime < 300 && deltaX > 50;
      const isLongSwipe = deltaX > 100;

      if (isFromLeftEdge && isRightSwipe && isHorizontal && (isFastSwipe || isLongSwipe)) {
        // Don't go back from root routes
        const rootRoutes = ['/login'];
        const isAtRoot = rootRoutes.some(route =>
          location.pathname === route
        ) || (
          location.pathname.startsWith('/w/') &&
          location.pathname.includes('/c/') &&
          !location.pathname.includes('/chat')
        );

        if (!isAtRoot && window.history.length > 1) {
          navigate(-1);
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [navigate, location.pathname]);

  return null;
}
